import "./styles.scss";

import moment from "moment";
import Popup from "reactjs-popup";
import { FaPlay } from "react-icons/fa";
import { FlagIcon } from "react-flag-kit";
import { TbRepeat } from "react-icons/tb";
import Dialog from "@material-ui/core/Dialog";
import { BiSolidError } from "react-icons/bi";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { AiOutlineLoading3Quarters } from "react-icons/ai";
import { Fragment, useEffect, useMemo, useState } from "react";
import { MdKeyboardArrowDown, MdKeyboardArrowUp } from "react-icons/md";

import aiIcon from "../../../assets/images/ai-icon.png";
import { findItemByStatus } from "./util/findItemByStatus";
import ModalMyHistoryActions from "../ModalMyHistoryActions";
import LoadingComponent from "../../LoadingComponent/LoadingComponent";
import { getContentExercises, regenerateContentExercises } from "../../../store/middleware";
import { setContentFilesExercisesSelected } from "../../../store/slices/contentExercises.slice";

const TIME_INTERVAL_GENERATE = 20000;
const LANGUAGE_FLAG = { EN: "US", PT: "BR", ES: "ES", FR: "FR" };

function ModalMyHistory({ showModal, setShowModal, language, userId, handleOpenModalGenerateExercisesWithAI }) {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { contentFilesExercises } = useSelector((state) => state.contentExercisesReducer);

  const [selectedRow, setSelectedRow] = useState(null);
  const [expanded, setExpanded] = useState(() => new Set());
  const [showModalMyHistoryActions, setShowModalMyHistoryActions] = useState(false);

  const rows = useMemo(
    () =>
      (contentFilesExercises || [])?.map((exercise) => {
        const isAgrouped = exercise.agrouped;
        const isGenerating = exercise.status === "generating" || exercise.status === "processing";
        const roadmap = (isAgrouped ? exercise.items[0].result?.roadmap : exercise?.result?.roadmap) ?? "-";

        return {
          ...exercise,
          id: isAgrouped ? exercise.items[0].id : exercise.id,
          suggestions: isGenerating ? t("GENERATING") : roadmap,
          score: isAgrouped ? exercise.items[0].score : exercise.score,
          createdAt: moment(exercise.createdAt).format(t("DATE_FORMAT") ?? "MM/DD/YYYY"),
          attempts:
            exercise?.items?.map((item) => ({
              ...item,
              createdAt: moment(item.createdAt).format(t("DATE_FORMAT") ?? "MM/DD/YYYY"),
            })) || [],
        };
      }),
    [contentFilesExercises, t],
  );

  const hasGeneratingRows = useMemo(
    () =>
      rows.some((row) => {
        if (row.agrouped) return row.items.some((item) => item.status === "generating" || item.status === "processing");
        return row.status === "generating" || row.status === "processing";
      }),
    [rows],
  );

  const closeModal = () => setShowModal(false);

  const getData = async () => {
    if (!language || !userId) return;

    const params = { language, limit: 20, userId };
    await dispatch(getContentExercises(params));
  };

  const toggleExpanded = (rowKey) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(rowKey)) next.delete(rowKey);
      else next.add(rowKey);
      return next;
    });
  };

  const handlePlay = async (row) => {
    const pendingItem = findItemByStatus(row, "pending");
    const target = pendingItem || row;

    await dispatch(setContentFilesExercisesSelected(target));
    handleOpenModalGenerateExercisesWithAI();
    closeModal();
  };

  const handleTryAgain = async (row) => {
    const failedItem = findItemByStatus(row, "failed");
    const exerciseId = failedItem?.id ?? row.id;
    if (!exerciseId) return;

    const payload = { userId, exerciseId };
    await dispatch(regenerateContentExercises(payload));
    await getData();
  };

  const handleResultHtml = async (row) => {
    const resultHtml = row.agrouped ? row.items[0].resultHtml : row.resultHtml;
    if (!row || !resultHtml) return;

    const payload = { ...row, exerciseHtml: resultHtml };
    await dispatch(setContentFilesExercisesSelected(payload));

    handleOpenModalGenerateExercisesWithAI();
    closeModal();
  };

  const handleColorScore = (row) => {
    const score = Number(row.agrouped ? row.items[0].score : row.score) ?? -1;
    if (score >= 70) return "green";
    if (score >= 30) return "yellow";
    if (score <= 30 && score >= 0) return "red";
    return "";
  };

  useEffect(() => {
    getData();
    // eslint-disable-next-line
  }, [showModal, language, userId]);

  useEffect(() => {
    if (!showModal || !hasGeneratingRows || !language || !userId) return;

    const intervalId = setInterval(() => {
      dispatch(getContentExercises({ language, limit: 20, userId }));
    }, TIME_INTERVAL_GENERATE);

    return () => clearInterval(intervalId);
    // eslint-disable-next-line
  }, [hasGeneratingRows, language, showModal]);

  const renderPopup = (value, max) => {
    if (value === "-") return <p style={{ textAlign: "center" }}>-</p>;

    return (
      <Popup
        on={"hover"}
        position={"top center"}
        className="popupSuggestions"
        trigger={<p className="popupText">{value.length > max ? value?.slice(0, max).concat("...") : value}</p>}
      >
        <div>
          <p>{value}</p>
        </div>
      </Popup>
    );
  };

  const renderTooltip = (value, mensage) => {
    if (value === "-") return <p style={{ color: "#000" }}>-</p>;
    return (
      <Popup on={"hover"} className="popupScore" position={"top center"} trigger={<p>{value}</p>}>
        <div>
          <p style={{ textAlign: "center" }}>{mensage}</p>
        </div>
      </Popup>
    );
  };

  return (
    <Dialog open={showModal} onClose={closeModal} className="modalMyHistory">
      <LoadingComponent />
      <div className="containerModalMyHistory">
        <div className="myHistoryHeader">
          <div className="myHistoryHeaderLeft">
            <img className="myHistoryIcon" src={aiIcon} alt="AI Icon" />
            <div className="myHistoryTitle">{t("MY_HISTORY_EXERCISES")}</div>
          </div>

          <FlagIcon size={40} code={LANGUAGE_FLAG[language]} />
        </div>

        <div className="myHistoryTable">
          <div className="myHistoryTableBody">
            <table className="myHistoryTableEl">
              <colgroup>
                <col className="col topics" />
                <col className="col created" />
                <col className="col updated" />
                <col className="col score" />
                <col className="col suggestions" />
                <col className="col options" />
              </colgroup>

              <thead>
                <tr className="myHistoryTableHeaderRow">
                  <th className="col topics">{t("MY_HISTORY_TOPICS")}</th>
                  <th className="col created">{t("MY_HISTORY_CREATION_DATE")}</th>
                  <th className="col updated">{t("CODE")}</th>
                  <th className="col score">{t("MY_HISTORY_SCORE")}</th>
                  <th className="col suggestions center">{t("MY_HISTORY_SUGGESTIONS")}</th>
                  <th className="col options center">{t("OPTIONS")}</th>
                </tr>
              </thead>

              <tbody>
                {rows.length > 0 ? (
                  rows.map((row, rowIndex) => {
                    const hasAttempts = row.agrouped;
                    const rowKey = `${row.id}-${rowIndex}`;
                    const isExpanded = expanded.has(rowKey);
                    const topics = row.exerciseTitle?.split(": ")[1]?.split(",");
                    const isDone = hasAttempts
                      ? row.items.some((item) => item.status === "done")
                      : row.status === "done";
                    const isPending = hasAttempts
                      ? row.items.some((item) => item.status === "pending")
                      : row.status === "pending";
                    const isFailed = hasAttempts
                      ? row.items.some((item) => item.status === "failed")
                      : row.status === "failed";
                    const isGenerating = hasAttempts
                      ? row.items.some((item) => item.status === "generating" || item.status === "processing")
                      : row.status === "generating" || row.status === "processing";

                    return (
                      <Fragment key={rowKey}>
                        <tr className={`myHistoryRow ${isGenerating ? "isGenerating" : ""}`}>
                          <td className="col topics ">
                            <p className="topicsLine">
                              {topics?.map((topic, index) => (
                                <span key={topic}>
                                  {topic}
                                  {index < topics.length - 1 && ","}
                                  {index < topics.length - 1 && <br />}
                                </span>
                              )) ?? "-"}
                            </p>
                          </td>

                          <td className="col created">{row.createdAt}</td>

                          <td className="col updated">
                            {isGenerating ? (
                              <div className="generatingCell">
                                <AiOutlineLoading3Quarters className="generatingSpinner" size={18} />
                              </div>
                            ) : (
                              row.id
                            )}
                          </td>

                          <td className={"col score"}>
                            <button
                              type="button"
                              onClick={() => handleResultHtml(row)}
                              className={`resultBtn ${handleColorScore(row)}`}
                            >
                              {renderTooltip(row.score ?? "-", t("MY_HISTORY_VIEW_RESULTS"))}
                            </button>
                          </td>

                          <td className={`col suggestions ${isGenerating ? "isGeneratingText" : ""}`}>
                            {renderPopup(row.suggestions, 110)}
                          </td>

                          <td className="col options">
                            {isPending && (
                              <button type="button" className="actionBtn" onClick={() => handlePlay(row)}>
                                <FaPlay size={12} />
                                <span>{t("START")}</span>
                              </button>
                            )}

                            {isDone && !isPending && (
                              <button
                                type="button"
                                className={`actionBtn iconOnly ${handleColorScore(row)}`}
                                onClick={() => {
                                  setSelectedRow(row);
                                  setShowModalMyHistoryActions(true);
                                }}
                              >
                                <TbRepeat size={20} />
                              </button>
                            )}

                            {isFailed && !isPending && (
                              <button
                                type="button"
                                className={"actionBtn iconOnly"}
                                onClick={() => handleTryAgain(row)}
                              >
                                <BiSolidError size={20} color="#ff4d4f" />
                                <span style={{ color: "#ff4d4f" }}>{t("TRY_AGAIN")}</span>
                              </button>
                            )}

                            {!isGenerating && hasAttempts && !isPending && (
                              <button
                                type="button"
                                className="expandBtn"
                                onClick={() => toggleExpanded(rowKey)}
                                aria-label={isExpanded ? "Collapse" : "Expand"}
                              >
                                {isExpanded ? <MdKeyboardArrowUp size={22} /> : <MdKeyboardArrowDown size={22} />}
                              </button>
                            )}
                          </td>
                        </tr>

                        {isExpanded &&
                          hasAttempts &&
                          row.attempts.map((attempt) => (
                            <tr
                              key={attempt.id}
                              className="myHistoryRow myHistorySubRow"
                              style={{ backgroundColor: "#cccccc20" }}
                            >
                              <td className="col topics" />
                              <td className="col created">{attempt.createdAt}</td>
                              <td className="col updated">{attempt.id}</td>
                              <td className={`col score ${handleColorScore(attempt)}`}>
                                <button
                                  type="button"
                                  onClick={() => handleResultHtml(attempt)}
                                  className={`resultBtn ${handleColorScore(attempt)}`}
                                >
                                  {attempt.score ?? "-"}
                                </button>
                              </td>
                              <td className="col suggestions">{renderPopup(attempt.result?.roadmap, 110)}</td>
                              <td className="col options" />
                            </tr>
                          ))}
                      </Fragment>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={6} className="emptyState">
                      {t("MY_HISTORY_NO_ITEMS_FOUND")}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="myHistoryFooter">
          <button type="button" className="backBtn" onClick={closeModal}>
            {t("BACK")}
          </button>
        </div>
      </div>

      <ModalMyHistoryActions
        data={selectedRow}
        refetchData={getData}
        onCloseModalMyHistory={closeModal}
        showModal={showModalMyHistoryActions}
        setShowModal={setShowModalMyHistoryActions}
        handleOpenModalGenerateExercisesWithAI={handleOpenModalGenerateExercisesWithAI}
      />
    </Dialog>
  );
}

export default ModalMyHistory;
